/**
 * 단지(Town) 관련 타입 정의
 */

/** 단지 상태 */
export type TownStatus = 'active' | 'synced' | 'excluded';

/** 디바이스 종류 (13종) */
export type DeviceType =
  | '비상벨485'
  | '일반비상벨'
  | '링고벨'
  | '중계기비상벨'
  | '무선비상벨'
  | '일반원패스'
  | '바원패스'
  | 'EV버튼'
  | '비콘'
  | '출입통제'
  | 'FMCS'
  | '옥상개폐기'
  | '전등제어';

/** 13종 디바이스 타입 목록 */
export const DEVICE_TYPES: readonly DeviceType[] = [
  '비상벨485',
  '일반비상벨',
  '링고벨',
  '중계기비상벨',
  '무선비상벨',
  '일반원패스',
  '바원패스',
  'EV버튼',
  '비콘',
  '출입통제',
  'FMCS',
  '옥상개폐기',
  '전등제어',
] as const;

/** 서버 타입 */
export type ServerType = 'RACK' | '데스크탑' | '미니PC' | '클라우드';

/** 구분 (사업 구분) */
export type CategoryOption =
  | 'KYS(B2B)'
  | 'KYS(B2C)'
  | '유니콤'
  | '이엠테크'
  | '신관산업'
  | '럭스로보'
  | '삼우'
  | '라온';

/** 발주처 */
export type OrdererOption =
  | '자이'
  | '코콤'
  | '효성'
  | '대우'
  | '퍼니포커스'
  | '유니콤'
  | '이엠테크'
  | '신관'
  | '럭스로보'
  | '오이패스'
  | '아파트너'
  | '트러스테이'
  | '아파트스토리';

/** 계약 유형 */
export type ContractTypeOption =
  | '납품계약'
  | '유지계약'
  | '단지계약'
  | '세대계약'
  | '개별계약';

/** 공사팀 담당자 */
export type ConstructionStaffOption =
  | '홍승우'
  | '정찬은'
  | '이승현'
  | '장성민'
  | '이현철';

/** 세팅 담당자 */
export type SettingStaffOption =
  | '이현진'
  | '류지민'
  | '장지안'
  | '김호정'
  | '박유찬';

/** 실물키 모델 */
export type PhysicalKeyModelOption =
  | 'X(미사용)'
  | 'DKM-3015'
  | 'DKC-3100'
  | 'DKC-3101S'
  | 'DKC-3102S'
  | 'DKM-3100K'
  | 'DKC-3101K'
  | 'HOS-200'
  | 'HOS-200P'
  | 'HOS-200P2'
  | 'HOS-200L';

/** 홈넷 업체 */
export type HomenetVendorOption =
  | '이지빌'
  | '자이'
  | '코콤'
  | 'Commax'
  | '경동원'
  | '계양정보통신'
  | '현대UPIS'
  | '현대통신'
  | 'HDC'
  | '액션테크'
  | '혜인'
  | '경동나비엔';

/** 감시반 업체 */
export type SurveillanceVendorOption =
  | '현대'
  | '네오이엔씨'
  | '나슬시스템'
  | 'NAVI'
  | '한기술'
  | '천조'
  | '성빈시스템'
  | '신보파워텍'
  | '코맥스'
  | 'BPN솔루션'
  | 'OTIS';

/** 하위 사용어플 */
export type SubAppOption =
  | '마이타운'
  | '자이패스'
  | '오이패스'
  | '노크타운'
  | '스마트원'
  | '아파트너'
  | '아파트스토리'
  | '스마트패스'
  | '코콤';

/** 단지 디바이스 사용 현황 */
export interface TownDevice {
  id?: number;
  town_id: number;
  device_type: DeviceType;
  is_used: boolean;
  count: number;
}

/** 단지 정보 (towns 테이블) */
export interface Town {
  id: number;
  sync_source_id: number | null;
  external_town_id: number | null;
  status: TownStatus;

  // 동기화 필드 (읽기전용: name, address, household_count, active_users_count)
  name: string;
  address: string | null;
  household_count: number;
  active_users_count: number;
  active_households: number;
  contracted_until: string | null; // DATE → YYYY-MM-DD string

  // 관리사무소 정보
  office_phone: string | null;
  office_fax: string | null;
  office_email: string | null;

  // 사용자 입력 필드
  vpn_number: string | null;
  category: string | null;
  orderer: string | null;
  sub_app: string | null;
  contract_type: string | null;
  construction_staff: string | null;
  setting_staff: string | null;
  installation_team: string | null;

  // 서버/장비 정보
  server_type: string | null;
  server_type_detail: string | null;
  server_version: string | null;
  server_version_aptset: string | null;
  tvbox_version: string | null;
  tvbox_details: Array<{ no: number; model: string; version: string }> | null;
  guard_box_count: number;

  // 업체/키 정보
  physical_key_model: string | null;
  homenet_vendor: string | null;
  surveillance_vendor: string | null;
  cctv_vendor: string | null;

  // 기능 사용 여부
  parking_location: boolean;
  hall_call: boolean;

  // 계약/일정
  delivery_date: string | null;
  delivery_date_unknown: boolean;
  setting_date: string | null;
  setting_date_unknown: boolean;
  contract_start_date: string | null;

  // 파일/메모
  contract_file_path: string | null;
  notes: string | null;

  // 메타데이터
  synced_at: string | null;
  created_at: string;
  updated_at: string;
}

/** 단지 수정 요청 (편집 가능 필드만) */
export interface TownUpdatePayload {
  office_phone?: string | null;
  office_fax?: string | null;
  office_email?: string | null;
  vpn_number?: string | null;
  category?: string | null;
  orderer?: string | null;
  sub_app?: string | null;
  contract_type?: string | null;
  construction_staff?: string | null;
  setting_staff?: string | null;
  installation_team?: string | null;
  contracted_until?: string | null;
  server_type?: string | null;
  server_type_detail?: string | null;
  server_version?: string | null;
  server_version_aptset?: string | null;
  tvbox_version?: string | null;
  tvbox_details?: Array<{ no: number; model: string; version: string }> | null;
  guard_box_count?: number;
  physical_key_model?: string | null;
  homenet_vendor?: string | null;
  surveillance_vendor?: string | null;
  cctv_vendor?: string | null;
  parking_location?: boolean;
  hall_call?: boolean;
  delivery_date?: string | null;
  delivery_date_unknown?: boolean;
  setting_date?: string | null;
  setting_date_unknown?: boolean;
  contract_start_date?: string | null;
  notes?: string | null;
}

/** 단지 목록 조회 쿼리 파라미터 */
export interface TownListQuery {
  page?: number;
  limit?: number;
  search?: string;
  category?: string;
  orderer?: string;
  contractType?: string;
  settingStaff?: string;
  status?: string; // 기본: 'active,synced'
  expiring?: boolean; // 30일 이내 만료 예정만
  sortBy?: string;   // 정렬 컬럼
  sortOrder?: 'asc' | 'desc'; // 정렬 방향
}

/** 읽기전용 동기화 필드 목록 */
export const READONLY_SYNCED_FIELDS = [
  'name',
] as const;

export type ReadonlySyncedField = (typeof READONLY_SYNCED_FIELDS)[number];
